import { Select } from "antd";
import axios from "axios";
import { useState } from "react";

import { showErrorNotification, showSuccessNotification } from "../../../utils/notification";
import { TAGS_API_BASE } from "../helper";

/** Prefix of the value of the extra option that creates the typed term, which no real tag can collide with. */
const CREATE_OPTION_VALUE_PREFIX = "__create__:";

/**
 * Picks one of the canonical tags a product line already holds, and creates a
 * new one from the same search box.
 *
 * A picker rather than a text box on purpose: typed freely, the same idea ends
 * up spelled three ways and the mappings stop collapsing onto one tag, which is
 * the drift this screen exists to undo. A term that matches no existing tag
 * adds one extra option, `+ Create "<term>"`, so a new canonical tag is always
 * a deliberate pick and never a typo that got saved.
 *
 * Search matches anywhere in a tag, case-insensitively. Creating posts the
 * trimmed, lowercased term, selects it and asks the parent to reload options.
 *
 * @param {object} props
 * @param {string} props.ROOT_URL
 * @param {string} props.tagDomain - Product line the new tag would join.
 * @param {string} props.value - Currently selected canonical tag, empty for none.
 * @param {Array<{label: string, value: string}>} props.canonicalTagOptions
 * @param {boolean} props.disabled
 * @param {string} [props.placeholder]
 * @param {(canonicalTag: string) => void} props.onChange - Called with an empty string when cleared.
 * @param {() => void} props.onCanonicalTagCreated - Reloads the option list after a new tag is added.
 */
const CanonicalTagSelect = ({
  ROOT_URL,
  tagDomain,
  value,
  canonicalTagOptions,
  disabled,
  placeholder,
  onChange,
  onCanonicalTagCreated
}) => {
  const [searchText, setSearchText] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const typedTerm = searchText.trim().toLowerCase();
  const doesTermMatchExistingTag = canonicalTagOptions.some(
    ({ value: existingTag }) => existingTag.toLowerCase() === typedTerm
  );
  const selectOptions =
    typedTerm && !doesTermMatchExistingTag
      ? [...canonicalTagOptions, { label: `+ Create "${typedTerm}"`, value: `${CREATE_OPTION_VALUE_PREFIX}${typedTerm}` }]
      : canonicalTagOptions;

  /**
   * Keeps an option when the typed text appears anywhere in it. The create
   * option always stays, since it exists only for the current term.
   *
   * @param {string} input - Text typed in the search box.
   * @param {{label: string, value: string}} option
   * @returns {boolean}
   */
  const filterOption = (input, { label, value: optionValue }) => {
    if (optionValue.startsWith(CREATE_OPTION_VALUE_PREFIX)) {
      return true;
    }

    return label.toLowerCase().includes(input.trim().toLowerCase());
  };

  /**
   * Adds a canonical tag to the product line, selects it, and reloads the
   * option list.
   *
   * @param {string} canonicalTag - Trimmed, lowercased term to create.
   */
  const handleCreate = (canonicalTag) => {
    setIsCreating(true);
    axios
      .post(`${ROOT_URL}/${TAGS_API_BASE}`, { tagDomain, canonicalTag })
      .then(() => {
        showSuccessNotification("Canonical tag added successfully!");
        onChange(canonicalTag);
        onCanonicalTagCreated();
      })
      .catch(showErrorNotification)
      .finally(() => setIsCreating(false));
  };

  /**
   * Routes a pick: the create option creates its term, any other option is a
   * plain selection, and a cleared picker reports an empty string.
   *
   * @param {string} [pickedValue] - Undefined when the picker was cleared.
   */
  const handleChange = (pickedValue) => {
    setSearchText("");

    if (pickedValue?.startsWith(CREATE_OPTION_VALUE_PREFIX)) {
      handleCreate(pickedValue.slice(CREATE_OPTION_VALUE_PREFIX.length));
      return;
    }

    onChange(pickedValue ?? "");
  };

  return (
    <Select
      value={value || undefined}
      allowClear
      showSearch
      disabled={disabled}
      loading={isCreating}
      style={{ width: "100%" }}
      placeholder={placeholder}
      options={selectOptions}
      filterOption={filterOption}
      onSearch={setSearchText}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setSearchText("");
        }
      }}
      onChange={handleChange}
      notFoundContent="No canonical tags yet. Type one to create it."
    />
  );
};

export default CanonicalTagSelect;
